'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, LogIn, Copy, Check } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import AppImage from '@/components/ui/AppImage';
import { useRouter } from 'next/navigation';
import { mockUsuarios } from '@/lib/mockData';
import { toast } from 'sonner';

interface LoginFormData {
  correo: string;
  contrasena: string;
  recordar: boolean;
}

const demoCredenciales = [
{ rol: 'Administrador', correo: 'admin@bahiabaliz.cr', contrasena: 'Admin2026!' },
{ rol: 'Socio', correo: 'carolina@bahiabaliz.cr', contrasena: 'Socio2026!' },
{ rol: 'Socio (debe)', correo: 'roberto@bahiabaliz.cr', contrasena: 'Socio2026!' }];


export default function LoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const router = useRouter();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    setError
  } = useForm<LoginFormData>();

  const handleUsarCredencial = (correo: string, contrasena: string) => {
    setValue('correo', correo);
    setValue('contrasena', contrasena);
  };

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(key);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const onSubmit = async (data: LoginFormData) => {
    setLoading(true);
    // Backend integration point: POST /api/auth/login with { correo, contrasena }
    await new Promise((r) => setTimeout(r, 1000));

    const usuario = mockUsuarios.find(
      (u) => u.correo === data.correo && u.contrasena === data.contrasena
    );

    if (!usuario) {
      setLoading(false);
      setError('correo', {
        message: 'Credenciales inválidas — usá las cuentas demo de abajo para ingresar'
      });
      return;
    }

    toast.success(`Bienvenido/a, ${usuario.nombre}`);
    setLoading(false);

    if (usuario.rol === 'admin') {
      // Store session: Backend integration point — set session cookie/token
      if (typeof window !== 'undefined') {
        localStorage.setItem('bahiabaliz_user', JSON.stringify({ id: usuario.id, rol: usuario.rol, nombre: usuario.nombre }));
      }
      router.push('/admin-panel');
    } else {
      if (typeof window !== 'undefined') {
        localStorage.setItem('bahiabaliz_user', JSON.stringify({ id: usuario.id, rol: usuario.rol, nombre: usuario.nombre }));
      }
      router.push('/socio-panel');
    }
  };

  return (
    <>
      {/* Left panel — brand image */}
      <div className="hidden lg:flex lg:w-1/2 relative flex-col justify-end ocean-section p-12">
        <AppImage
          src="https://images.unsplash.com/photo-1532016967111-4c55a3f1e639"
          alt="Vista aérea del océano Pacífico desde Herradura Costa Rica con palmeras y cielo azul"
          fill
          priority
          className="object-cover"
          sizes="50vw" />
        
        <div className="absolute inset-0 bg-hero-gradient" />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-6">
            <AppLogo size={44} />
            <span className="font-display text-2xl font-semibold text-white">Bahía Baliz</span>
          </div>
          <p className="font-display text-3xl font-bold text-white mb-3 leading-tight">
            Tu refugio en<br />el Pacífico costarricense
          </p>
          <p className="text-white/60 text-base">
            Pacific Sun · Herradura, Jacó · Costa Rica
          </p>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12 bg-background">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <AppLogo size={36} />
            <span className="font-display text-xl font-semibold text-primary">Bahía Baliz</span>
          </div>

          <h1 className="font-display text-2xl font-bold text-foreground mb-1">
            Acceso socios
          </h1>
          <p className="text-muted-foreground text-sm mb-8">
            Ingresá con tu correo y contraseña para acceder a tu panel.
          </p>

          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
            {/* Correo */}
            <div>
              <label htmlFor="correo" className="block text-sm font-medium text-foreground mb-1.5">
                Correo electrónico
              </label>
              <input
                id="correo"
                type="email"
                autoComplete="email"
                placeholder="tu@correo.com"
                className={`w-full px-4 py-2.5 rounded-lg border bg-input text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors ${
                errors.correo ? 'border-destructive' : 'border-border'}`
                }
                {...register('correo', {
                  required: 'El correo es requerido',
                  pattern: { value: /^\S+@\S+\.\S+$/, message: 'Correo inválido' }
                })} />
              
              {errors.correo &&
              <p className="text-destructive text-xs mt-1">{errors.correo.message}</p>
              }
            </div>

            {/* Contraseña */}
            <div>
              <label htmlFor="contrasena" className="block text-sm font-medium text-foreground mb-1.5">
                Contraseña
              </label>
              <div className="relative">
                <input
                  id="contrasena"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Tu contraseña"
                  className={`w-full px-4 py-2.5 pr-11 rounded-lg border bg-input text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors ${
                  errors.contrasena ? 'border-destructive' : 'border-border'}`
                  }
                  {...register('contrasena', { required: 'La contraseña es requerida' })} />
                
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}>
                  
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.contrasena &&
              <p className="text-destructive text-xs mt-1">{errors.contrasena.message}</p>
              }
            </div>

            {/* Recordar */}
            <div className="flex items-center gap-2">
              <input
                id="recordar"
                type="checkbox"
                className="w-4 h-4 rounded border-border accent-primary"
                {...register('recordar')} />
              
              <label htmlFor="recordar" className="text-sm text-muted-foreground">
                Mantener sesión iniciada
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 disabled:opacity-60 transition-all scale-active">
              
              {loading ?
              <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" /> :

              <>
                  <LogIn size={16} />
                  Ingresar
                </>
              }
            </button>
          </form>

          {/* Demo credentials */}
          <div className="mt-8 p-4 rounded-xl bg-muted border border-border">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              Cuentas de demostración
            </p>
            <div className="space-y-2">
              {demoCredenciales.map((cred) =>
              <div
                key={`demo-${cred.correo}`}
                className="flex items-center justify-between gap-2 bg-card rounded-lg px-3 py-2 border border-border">
                
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-foreground">{cred.rol}</p>
                    <p className="text-xs text-muted-foreground truncate">{cred.correo}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                    type="button"
                    onClick={() => handleCopy(cred.correo, `correo-${cred.correo}`)}
                    className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    aria-label="Copiar correo">
                    
                      {copiedField === `correo-${cred.correo}` ? <Check size={12} className="text-success" /> : <Copy size={12} />}
                    </button>
                    <button
                    type="button"
                    onClick={() => handleUsarCredencial(cred.correo, cred.contrasena)}
                    className="px-2.5 py-1 rounded-md bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors">
                    
                      Usar
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <p className="text-center text-xs text-muted-foreground mt-6">
            ¿No tenés cuenta? Contactá al administrador de Bahía Baliz.
          </p>
        </div>
      </div>
    </>);

}